// src/pages/chat/settings/profile/upload-image/upload-image.tsx
import React, { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useUploadImage } from './upload-image.hooks';
import {
  Backdrop,
  Content,
  Header,
  Title,
  CloseBtn,
  Body,
  PreviewWrap,
  PreviewCircle,
  PreviewImg,
  PreviewHint,
  ControlsRow,
  Slider,
  SliderLabel,
  Footer,
  Btn,
  BtnPrimary,
  ErrorText,
} from './upload-image.styled';

type UploadImageModalProps = {
  open: boolean;
  file: File | null;
  onOpenChange: (open: boolean) => void;
  onSave: (jpegFile: File) => Promise<void> | void;
};

export const UploadImageModal: React.FC<UploadImageModalProps> = ({
  open,
  file,
  onOpenChange,
  onSave,
}) => {
  // IMPORTANT:
  // - Preview circle is 240px (see styled file)
  // - Export output is 600px
  // - Hook now maps preview-space interaction to output-space correctly
  const PREVIEW_SIZE = 240;

  const {
    error,
    previewUrl,
    imgLoaded,
    zoom,
    previewOffsetX,
    previewOffsetY,
    setError,
    onZoomChange,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    exportJpeg,
  } = useUploadImage({ file, maxSize: 600, previewSize: PREVIEW_SIZE });

  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => !!file && imgLoaded && !saving, [file, imgLoaded, saving]);

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    try {
      setError(null);
      if (!file) return;

      setSaving(true);

      const jpeg = await exportJpeg();
      await onSave(jpeg);
    } catch (e: any) {
      setError(e?.message || 'Failed to save image');
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Backdrop />
        <Content
          onPointerUp={onPointerUp}
          aria-describedby={undefined}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            Upload photo
          </Dialog.Title>
          <Header>
            <Title>Upload photo</Title>
            <Dialog.Close asChild>
              <CloseBtn type="button" aria-label="Close">
                ✕
              </CloseBtn>
            </Dialog.Close>
          </Header>

          <Body>
            <PreviewWrap>
              <PreviewCircle
                role="application"
                aria-label="Avatar crop preview"
                onPointerDown={(e) => {
                  try {
                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                  } catch {
                    // ignore
                  }
                  onPointerDown(e.clientX, e.clientY);
                }}
                onPointerMove={(e) => onPointerMove(e.clientX, e.clientY)}
              >
                {previewUrl ? (
                  <PreviewImg
                    src={previewUrl}
                    alt="Selected"
                    draggable={false}
                    // ✅ Live preview now uses PREVIEW-space offsets,
                    // while export uses OUTPUT-space offsets.
                    style={{
                      transform: `translate(calc(-50% + ${previewOffsetX}px), calc(-50% + ${previewOffsetY}px)) scale(${zoom})`,
                    }}
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <PreviewHint>Select an image to preview</PreviewHint>
                )}
              </PreviewCircle>

              <PreviewHint>Drag to reposition</PreviewHint>
            </PreviewWrap>

            <ControlsRow>
              <SliderLabel htmlFor="avatar_zoom">Zoom</SliderLabel>
              <Slider
                id="avatar_zoom"
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => onZoomChange(Number(e.target.value))}
                aria-label="Zoom"
              />
            </ControlsRow>

            {error ? <ErrorText>{error}</ErrorText> : null}
          </Body>

          <Footer>
            <Dialog.Close asChild>
              <Btn type="button" disabled={saving}>
                Cancel
              </Btn>
            </Dialog.Close>

            <BtnPrimary type="button" disabled={!canSave} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </BtnPrimary>
          </Footer>
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
